import React, { createContext, useReducer, useEffect } from "react";

import initialState from "./auth.state";
import authReducer from "./auth.reducer";
import { CLEAR_ALL_AUTH_STATE, LOADED_AUTH_STATE, LOADED_SCOPE, CLEAR_AUTH_STATE } from "./auth.types";
import { HttpService } from "@/services/http.services";
import { StorageService } from "@/services";
import { OauthApi } from "@/api";

export const AuthContext = createContext<any>(initialState);

export const AuthProvider = ({ children }) => {
    const [state, dispatchAuth] = useReducer(authReducer, initialState);

    useEffect(() => {
        const accessToken = StorageService.getAccessToken();
        if (accessToken) {
            loadAuthState(accessToken);
        } else {
            dispatchAuth({ type: CLEAR_ALL_AUTH_STATE });
            dispatchAuth({ type: CLEAR_AUTH_STATE });
        }
    }, []);

    useEffect(() => {
        if (state.userId) {
            loadScopesInfo(state.userId);
        }
    }, [state.userId, state.groupIds]);

    const loadAuthState = async (token: string) => {
        try {
            HttpService.setAccessToken(token);

            OauthApi.loadAuthState()
                .then((res) => {
                    let isAdmin = false;
                    let isUser = false;
                    if (res.data.data.user_type === "SERVICE_PROVIDER") {
                        // ROLE_USER
                        isAdmin = true;
                    } else {
                        isUser = true;
                    }

                    // User
                    dispatchAuth({
                        type: LOADED_AUTH_STATE,
                        payload: {
                            isAuthenticated: true,
                            isAdmin: isAdmin,
                            isUser: isUser,

                            user: res.data.data,
                            userId: res.data.data.user_id ? Number(res.data.data.user_id) : null,
                            userName: res.data.data.user_name,
                            userEmail: res.data.data.email,
                            userPhone: res.data.data.phone,
                            userType: res.data.data.user_type,
                            userOfficeLevel: res.data.data.user_office_level,
                            userSignature: res.data.data.user_signature,
                            userNid: res.data.data.user_nid,
                            isUserNidVerified: res.data.data.is_user_nid_verified,
                            userNidVerificationAttempt: res.data.data.nid_verification_attempts,

                            profileId: res.data.data.profile_id ? Number(res.data.data.profile_id) : null,
                            profileNameEn: res.data.data.profile_name_en,
                            profileNameBn: res.data.data.profile_name_bn,
                            profileType: res.data.data.profile_type,
                            profileImage: res.data.data.profile_image,

                            employeeId: res.data.data.employee_id,
                            dealerId: res.data.data.dealer_id,
                            supervisorId: res.data.data.supervisor_id,
                            designationId: res.data.data.designation_id ? Number(res.data.data.designation_id) : null,
                            designationEn: res.data.data.designation_name_en,
                            designationBn: res.data.data.designation_name_bn,
                            organogramDesignationId: res.data.data.organogram_designation_id,

                            organizationId: res.data.data.organization_id ? Number(res.data.data.organization_id) : null,
                            workOrganizationId: res.data.data.work_organization_id ? Number(res.data.data.work_organization_id) : null,
                            organizationNameEn: res.data.data.organization_name_en,
                            organizationNameBn: res.data.data.organization_name_bn,
                            organizationIds: res.data.data.organization_ids?.map((item) => Number(item)) || [],

                            organogramId: res.data.data.organogram_id ? Number(res.data.data.organogram_id) : null,
                            workOrganogramId: res.data.data.work_organogram_id ? Number(res.data.data.work_organogram_id) : null,
                            organogramType: res.data.data.organogram_type,
                            organogramNameEn: res.data.data.organogram_name_en,
                            organogramNameBn: res.data.data.organogram_name_bn,
                            organogramIds: res.data.data.organogram_ids?.map((item) => Number(item)) || [],

                            groupIds: res.data.data.group_ids?.map((item) => Number(item)) || [],
                            groupNameList: res.data.data.group_name_list,
                            groupCodeList: res.data.data.group_code_list,

                            roleIds: res.data.data.role_ids?.map((item) => Number(item)) || [],
                            roleNameList: res.data.data.role_name_list,
                            roleCodeList: res.data.data.role_code_list,

                            divisionId: res.data.data.division_id,
                            regionId: res.data.data.region_id,
                            districtId: res.data.data.district_id,
                            thanaId: res.data.data.thana_id,
                            unionId: res.data.data.union_id,
                            blockId: res.data.data.block_id,
                            latitude: res.data.data?.latitude || 0,
                            longitude: res.data.data?.longitude || 0,

                            riceCropId: res.data.data.rice_crop_id,
                            currentRiceSeasonId: res.data.data.current_rice_season_id,
                            currentCropSeasonId: res.data.data.current_crop_season_id,
                            currentFinancialYearId: res.data.data.current_financial_year_id,
                            currentFinancialYearTitle: res.data.data.current_financial_year_title,
                            nextFinancialYearId: res.data.data.next_financial_year_id,
                            nextFinancialYearTitle: res.data.data.next_financial_year_title,
                        },
                    });
                })
                .catch((err) => {
                    dispatchAuth({ type: CLEAR_ALL_AUTH_STATE });
                    dispatchAuth({ type: CLEAR_AUTH_STATE });
                });
        } catch (err) {
            dispatchAuth({ type: CLEAR_ALL_AUTH_STATE });
            dispatchAuth({ type: CLEAR_AUTH_STATE });
        }
    };

    const loadScopesInfo = (userId: any) => {
        const payload = {
            user_id: userId,
        };
        OauthApi.loadUserScopes(payload).then((res) => {
            dispatchAuth({
                type: LOADED_SCOPE,
                payload: {
                    scopes: res.data.data,
                },
            });
        });
    };

    const clearAuthState = () => {
        dispatchAuth({ type: CLEAR_ALL_AUTH_STATE });
        dispatchAuth({ type: CLEAR_AUTH_STATE });
    };

    const setLoading = (isShow: boolean): void => {
        if (isShow) {
            dispatchAuth({
                type: "SET_LOADING",
                payload: {
                    loading: true,
                },
            });
        } else {
            dispatchAuth({
                type: "SET_LOADING",
                payload: {
                    loading: false,
                },
            });
        }
    };

    return <AuthContext.Provider value={{ ...state, dispatchAuth, loadAuthState, clearAuthState, setLoading }}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
